import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";

import ClosableDialog from "components/shared/ClosableDialog";
import { Para16Text } from "components/shared/Typography";
import { CUSTOM_PANELS } from "helpers/apis";
import useAxios from "helpers/axios";
import { ICustomPanel } from "types/panel";

import useDraftCustomPanels from "../../useDraftCustomPanel";

interface IDeleteConfirmDialogProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  customPanel: ICustomPanel;
}

export const DeleteConfirmDialog: React.FC<IDeleteConfirmDialogProps> = ({
  open,
  setOpen,
  customPanel,
}) => {
  const [confirmText, setConfirmText] = useState<string>("");
  const { currentPage, fetch, setMessage } = useDraftCustomPanels();

  useEffect(() => {
    if (!open) {
      setConfirmText("");
    }
  }, [open]);

  const { fetchData } = useAxios({
    config: {
      url: CUSTOM_PANELS,
      method: "DELETE",
    },
    callOnInit: false,
  });

  const onClose = () => {
    setOpen(false);
  };

  const onDelete = () => {
    fetchData(
      {
        url: `${CUSTOM_PANELS}${customPanel.id}`,
      },
      () => {
        fetch(currentPage);
        setMessage("delete_succeed");
      },
      () => {
        setMessage("delete_failed");
      },
      () => {
        onClose();
      }
    );
  };

  return (
    <ClosableDialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        <Box mt={1}>
          <Typography variant="h2">Are you sure you want to delete?</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Para16Text>
          Once you delete this draft panel, you will NOT be able to retrieve it
          again. Please type DELETE to confirm.
        </Para16Text>
        <Box display="flex" mt={4} mb={1} columnGap={1}>
          <Typography variant="h5" component="h3">
            Panel Name
          </Typography>
          <Para16Text>{customPanel.name}</Para16Text>
        </Box>
        <TextField
          label="Type DELETE to confirm"
          fullWidth
          variant="filled"
          value={confirmText}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setConfirmText(event.target.value);
          }}
        />
      </DialogContent>

      <DialogActions>
        <Box
          display="flex"
          justifyContent="center"
          columnGap={2}
          pb={3}
          mt={-1}
          flex={1}
        >
          <Button
            onClick={onDelete}
            size="large"
            disabled={confirmText.toLowerCase() !== "delete"}
          >
            Delete Draft Custom Panel
          </Button>
          <Button variant="outlined" size="large" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </ClosableDialog>
  );
};
